import { useScrollToTop } from "@/hooks/useScrollToTop";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";

const PrivacyPolicy = () => {
  useScrollToTop();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Privacy Policy | Aureum VII Trading"
        description="Privacy policy and GDPR compliance information for Aureum VII Trading. Learn how we collect, use, and protect your personal data."
        keywords="privacy policy, GDPR, data protection, personal data, privacy rights"
        url="/privacy-policy"
      />
      <Navigation />
      
      <main className="container mx-auto px-4 py-20 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">Privacy Policy</h1>
        
        <Card className="p-8 space-y-8">
          <section>
            <p className="text-muted-foreground mb-6">
              Last Updated: {new Date().toLocaleDateString('en-GB')}
            </p>
            <p className="mb-6">
              Aureum VII Trading ("we," "our," or "us") is committed to protecting your privacy and 
              ensuring the security of your personal information. This Privacy Policy explains how we 
              collect, use, disclose, and safeguard your data in compliance with the General Data Protection 
              Regulation (GDPR) and applicable Spanish and European data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">1. Data Controller Information</h2>
            <p className="mb-2"><strong>Company Name:</strong> Aureum VII Trading</p>
            <p className="mb-2"><strong>Registered Address:</strong> Marbella, Andalusia, Spain</p>
            <p className="mb-2"><strong>Contact Email:</strong> <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline">info@aureum7trading.com</a></p>
            <p className="mb-2"><strong>Phone:</strong> +34 688 495 309</p>
            <p className="mb-4"><strong>Website:</strong> <a href="https://www.aureum7trading.com" className="text-primary hover:underline">aureum7trading.com</a></p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">2.1 Personal Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Contact Information:</strong> Name, email address, phone number, company name, postal address</li>
              <li><strong>Account Information:</strong> Username, password, preferences (if you create an account)</li>
              <li><strong>Order Information:</strong> Billing address, shipping address, payment details, purchase history</li>
              <li><strong>Communication Data:</strong> Messages, inquiries, customer support requests, feedback</li>
              <li><strong>Professional Information:</strong> Company type, industry, project details (for B2B customers)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">2.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on site, referral source, click patterns</li>
              <li><strong>Cookies and Tracking:</strong> Session cookies, preference cookies, analytics cookies</li>
              <li><strong>Location Data:</strong> Approximate geographic location based on IP address</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use your personal data for the following purposes:</p>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">3.1 Order Processing & Customer Service</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Process and fulfill your orders</li>
              <li>Manage payments and prevent fraud</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Send order confirmations, shipping updates, and delivery notifications</li>
              <li>Handle returns, refunds, and warranty claims</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">3.2 Marketing & Communication</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Send promotional emails about new products, offers, and services (with your consent)</li>
              <li>Provide personalized product recommendations</li>
              <li>Send newsletters and company updates</li>
              <li>Contact you about related products or services that may interest you</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">3.3 Website Improvement & Analytics</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Analyze website usage to improve user experience</li>
              <li>Monitor website performance and troubleshoot issues</li>
              <li>Conduct market research and gather feedback</li>
              <li>Develop new features and services</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">3.4 Legal & Security</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Comply with legal obligations and regulations</li>
              <li>Prevent fraud, spam, and abuse</li>
              <li>Protect our rights and property</li>
              <li>Resolve disputes and enforce agreements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Legal Basis for Processing (GDPR)</h2>
            <p className="mb-4">We process your personal data based on the following legal grounds:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Contract Performance:</strong> Processing necessary to fulfill our contract with you (orders, delivery, customer service)</li>
              <li><strong>Consent:</strong> You have given explicit consent for marketing communications or cookies</li>
              <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests (website analytics, fraud prevention, improving services)</li>
              <li><strong>Legal Obligation:</strong> Processing required to comply with laws and regulations (tax, accounting, consumer protection)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. Cookies and Tracking Technologies</h2>
            <p className="mb-4">We use cookies and similar technologies to enhance your experience:</p>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">5.1 Types of Cookies We Use</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Essential Cookies:</strong> Required for website functionality (shopping cart, login sessions)</li>
              <li><strong>Performance Cookies:</strong> Collect data on website usage to improve performance</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Marketing Cookies:</strong> Track your browsing to show relevant ads (with your consent)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.2 Third-Party Cookies</h3>
            <p className="mb-4">We use third-party services that may set cookies:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Google Analytics:</strong> Website traffic analysis</li>
              <li><strong>Facebook Pixel:</strong> Social media advertising and tracking</li>
              <li><strong>Payment Processors:</strong> Secure payment processing</li>
            </ul>
            <p className="mt-4">
              You can manage cookie preferences through your browser settings or our cookie consent banner.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. How We Share Your Information</h2>
            <p className="mb-4">We may share your personal data with:</p>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">6.1 Service Providers</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Payment processors (for secure transactions)</li>
              <li>Shipping and logistics companies (for order delivery)</li>
              <li>Email service providers (for marketing and transactional emails)</li>
              <li>Cloud hosting providers (for data storage)</li>
              <li>Customer support platforms (for service tickets)</li>
              <li>Analytics providers (for website performance)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">6.2 Business Partners</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Manufacturers and suppliers (for product fulfillment)</li>
              <li>Installation partners (if you request professional installation)</li>
              <li>Marketing partners (with your consent)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">6.3 Legal Requirements</h3>
            <p className="mb-4">
              We may disclose your information if required by law, court order, or government request, 
              or to protect our rights, safety, or property.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">7. Data Security</h2>
            <p className="mb-4">We implement industry-standard security measures to protect your data:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure servers with firewall protection</li>
              <li>Restricted access to personal data (authorized personnel only)</li>
              <li>Regular security audits and updates</li>
              <li>Encrypted storage of sensitive information</li>
              <li>Secure payment processing (PCI DSS compliant)</li>
            </ul>
            <p className="mt-4">
              While we strive to protect your data, no method of transmission over the internet is 100% secure. 
              We cannot guarantee absolute security but take all reasonable precautions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">8. Data Retention</h2>
            <p className="mb-4">We retain your personal data for as long as necessary to fulfill the purposes outlined in this policy:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Order Data:</strong> 7 years (for tax and accounting purposes)</li>
              <li><strong>Customer Accounts:</strong> Until account deletion or 3 years of inactivity</li>
              <li><strong>Marketing Data:</strong> Until you withdraw consent or 2 years of inactivity</li>
              <li><strong>Support Inquiries:</strong> 2 years after resolution</li>
              <li><strong>Website Analytics:</strong> 26 months (Google Analytics default)</li>
            </ul>
            <p className="mt-4">
              After the retention period, we will securely delete or anonymize your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">9. Your Rights Under GDPR</h2>
            <p className="mb-4">You have the following rights regarding your personal data:</p>
            
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Right to Access:</strong> Request a copy of your personal data we hold</li>
              <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of your data</li>
              <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
              <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests or for direct marketing</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for marketing or cookies at any time</li>
              <li><strong>Right to Lodge a Complaint:</strong> File a complaint with the Spanish Data Protection Authority (AEPD)</li>
            </ul>

            <p className="mt-4">
              To exercise any of these rights, contact us at <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline">info@aureum7trading.com</a>. 
              We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">10. International Data Transfers</h2>
            <p className="mb-4">
              Your data is primarily stored within the European Economic Area (EEA). If we transfer data 
              outside the EEA, we ensure adequate safeguards are in place:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>EU-approved Standard Contractual Clauses (SCCs)</li>
              <li>EU-US Data Privacy Framework certification</li>
              <li>Adequacy decisions by the European Commission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">11. Children's Privacy</h2>
            <p>
              Our services are not directed to individuals under 18 years of age. We do not knowingly 
              collect personal data from children. If you believe we have inadvertently collected data 
              from a minor, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page 
              with a revised "Last Updated" date. Significant changes will be communicated via email or 
              prominent website notice. Continued use of our services after changes constitutes acceptance 
              of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">13. Contact Us</h2>
            <p className="mb-4">For questions about this Privacy Policy or to exercise your rights:</p>
            <ul className="space-y-2">
              <li><strong>Email:</strong> <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline">info@aureum7trading.com</a></li>
              <li><strong>Phone:</strong> +34 688 495 309</li>
              <li><strong>Address:</strong> Aureum VII Trading, Marbella, Andalusia, Spain</li>
            </ul>
            <p className="mt-4">
              <strong>Data Protection Authority:</strong> You may also contact the Spanish Data Protection 
              Authority (Agencia Española de Protección de Datos - AEPD) at 
              <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                www.aepd.es
              </a>
            </p>
          </section>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
